// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AppIcon,
  Badge,
  Button,
  DataTable,
  Dropdown,
  IconButton,
  Modal,
  Spinner,
  TextArea,
  TextInput,
} from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { chartPalette } from '@loa/design-tokens';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fundsApi, FundDTO, RiskProfile, StrategyDTO } from '../api/fundsApi';
import { FundSearchSelect } from '../components/funds/FundSearchSelect';
import { computePortfolioRisk } from '../components/funds/portfolioRisk';
import { PortfolioRiskBadge } from '../components/funds/PortfolioRiskBadge';

/** Mirrors funds.strategy.allocation-tolerance (server-enforced on publish). */
const ALLOCATION_TOLERANCE = 0.01;

interface BuilderLine {
  fund: FundDTO;
  /** Raw input string so partial numbers ("12.") survive typing. */
  weight: string;
}

const RISK_OPTIONS: { value: string; text: string }[] = [
  { value: '', text: 'No risk profile' },
  { value: 'CONSERVATIVE', text: 'Conservative' },
  { value: 'BALANCED', text: 'Balanced' },
  { value: 'GROWTH', text: 'Growth' },
  { value: 'ADVENTUROUS', text: 'Adventurous' },
];

/**
 * Portfolio Builder — Figma Advisor 2.0 node 978-37758 (Fund Center UI 2.0
 * PRD FR-12..12c). Selected-funds table with per-row Weight % + delete, a
 * pinned total indicator, Save This Portfolio (name/risk collected in the
 * save dialog), publish for saved drafts, and the calendar-years Fund
 * Returns chart. PUBLISHED/ARCHIVED portfolios render read-only with a
 * Clone CTA.
 */
export default function PortfolioBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [lines, setLines] = useState<BuilderLine[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [riskProfile, setRiskProfile] = useState('');
  const [status, setStatus] = useState<StrategyDTO['status'] | null>(null);
  const [scope, setScope] = useState<StrategyDTO['scope'] | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const readOnly = status != null && (status !== 'DRAFT' || scope === 'BROKERAGE');

  // ── Load: existing portfolio (edit) or catalogue selection (new) ──────
  useEffect(() => {
    let cancelled = false;

    const hydrate = async (fundKeys: string[], weights?: Map<string, number>) => {
      const results = await Promise.allSettled(fundKeys.map((k) => fundsApi.get(k)));
      if (cancelled) return;
      const hydrated: BuilderLine[] = [];
      const missing: string[] = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          const pct = weights?.get(r.value.fundKey);
          hydrated.push({ fund: r.value, weight: pct != null ? String(pct) : '' });
        } else {
          missing.push(fundKeys[i]);
        }
      });
      setLines(hydrated);
      if (missing.length > 0) {
        setNotice(`Some funds could not be loaded from the catalogue: ${missing.join(', ')}`);
      }
    };

    (async () => {
      try {
        setLoading(true);
        setError(null);
        if (id) {
          const strategy = await fundsApi.getStrategy(id);
          if (cancelled) return;
          setName(strategy.name);
          setDescription(strategy.description ?? '');
          setRiskProfile(strategy.riskProfile ?? '');
          setStatus(strategy.status);
          setScope(strategy.scope);
          const weights = new Map(
            strategy.components.map((c) => [c.fundKey, c.allocationPct ?? 0])
          );
          await hydrate(
            strategy.components.map((c) => c.fundKey),
            weights
          );
        } else {
          const preselected: string[] =
            (location.state as { fundKeys?: string[] } | null)?.fundKeys ?? [];
          if (preselected.length > 0) {
            await hydrate(preselected);
          }
        }
      } catch (e: unknown) {
        if (!cancelled) setError(extractApiMessage(e, 'Failed to load the portfolio'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // location.state is intentionally read once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addFund = useCallback((fund: FundDTO) => {
    setLines((prev) => {
      if (prev.some((l) => l.fund.fundKey === fund.fundKey)) {
        setNotice(`${fund.fundName} is already in the portfolio.`);
        return prev;
      }
      return [...prev, { fund, weight: '' }];
    });
  }, []);

  const removeFund = (fundKey: string) => {
    setLines((prev) => prev.filter((l) => l.fund.fundKey !== fundKey));
  };

  const setWeight = (fundKey: string, weight: string) => {
    setLines((prev) => prev.map((l) => (l.fund.fundKey === fundKey ? { ...l, weight } : l)));
  };

  const totalWeight = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l.weight) || 0), 0),
    [lines]
  );
  const totalOk = Math.abs(totalWeight - 100) <= ALLOCATION_TOLERANCE;

  const riskResult = useMemo(
    () =>
      computePortfolioRisk(
        lines.map((l) => ({ riskRating: l.fund.riskRating, weight: Number(l.weight) || 0 }))
      ),
    [lines]
  );

  const buildRequest = () => ({
    name: name.trim(),
    description: description.trim() || null,
    riskProfile: (riskProfile || null) as RiskProfile | null,
    components: lines.map((l, i) => ({
      fundKey: l.fund.fundKey,
      allocationPct: Number(l.weight) || 0,
      sortOrder: i,
    })),
  });

  const save = async () => {
    try {
      setBusy(true);
      setSaveError(null);
      const req = buildRequest();
      const saved = id
        ? await fundsApi.updateStrategy(id, req)
        : await fundsApi.createStrategy(req);
      setSaveOpen(false);
      navigate(`/funds/portfolios/${saved.id}`, { replace: true });
      setStatus(saved.status);
      setScope(saved.scope);
      setNotice(`"${saved.name}" saved as a draft.`);
    } catch (e: unknown) {
      setSaveError(extractApiMessage(e, 'Failed to save the portfolio'));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!id) return;
    try {
      setBusy(true);
      setError(null);
      // Persist the latest edits before publishing so the server validates
      // what the advisor sees.
      await fundsApi.updateStrategy(id, buildRequest());
      const published = await fundsApi.publishStrategy(id);
      setStatus(published.status);
      setNotice(`"${published.name}" published.`);
    } catch (e: unknown) {
      setError(extractApiMessage(e, 'Failed to publish the portfolio'));
    } finally {
      setBusy(false);
    }
  };

  const clone = async () => {
    if (!id) return;
    try {
      setBusy(true);
      const created = await fundsApi.cloneStrategy(id);
      navigate(`/funds/portfolios/${created.id}`);
      setStatus(created.status);
      setScope(created.scope);
      setNotice(`Cloned into a new draft.`);
    } catch (e: unknown) {
      setError(extractApiMessage(e, 'Failed to clone the portfolio'));
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<BuilderLine>[] = [
    {
      key: 'fund',
      header: 'Fund Name',
      render: (l) => (
        <div>
          <div className="font-medium">{l.fund.fundName}</div>
          <div className="text-body-04 text-grey-03">{l.fund.fundKey}</div>
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (l) => l.fund.providerDisplayName || l.fund.providerCode,
    },
    {
      key: 'category',
      header: 'Category',
      render: (l) => l.fund.categoryName || '—',
    },
    {
      key: 'price',
      header: 'Price',
      width: '90px',
      render: (l) => (l.fund.price != null ? l.fund.price.toFixed(4) : '—'),
    },
    {
      key: 'priceDate',
      header: 'Price Date',
      width: '110px',
      render: (l) => (l.fund.priceDate ? formatDate(l.fund.priceDate) : '—'),
    },
    {
      key: 'weight',
      header: 'Weight',
      width: '140px',
      render: (l) => (
        <span className="flex items-center gap-xs">
          <TextInput
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={l.weight}
            onChange={(e) => setWeight(l.fund.fundKey, e.target.value)}
            disabled={readOnly}
            aria-label={`Weight for ${l.fund.fundName}`}
          />
          <span className="text-body-03 text-grey-02">%</span>
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '80px',
      render: (l) => (
        <IconButton
          variant="ghost"
          aria-label={`Remove ${l.fund.fundName}`}
          onClick={() => removeFund(l.fund.fundKey)}
          disabled={readOnly}
          icon={<AppIcon name="trash" size={18} />}
        />
      ),
    },
  ];

  const chartData = useMemo(() => buildChartData(lines), [lines]);

  if (loading) {
    return (
      <div className="mx-lg my-lg" data-testid="portfolio-builder-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-lg my-lg flex flex-col gap-lg" data-testid="portfolio-builder-page">
      <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-lg">
        <div className="flex items-center justify-between gap-md flex-wrap">
          <div className="flex items-center gap-sm min-w-0">
            <IconButton
              variant="ghost"
              aria-label="Back to Fund Center"
              onClick={() => navigate('/funds')}
              icon={<AppIcon name="arrow left" size={20} />}
            />
            <h1 className="m-0 text-body-01 font-medium">Portfolio Builder</h1>
            {status && (
              <Badge color={status === 'PUBLISHED' ? 'green' : status === 'DRAFT' ? 'blue' : 'grey'} size="sm">
                {status}
              </Badge>
            )}
            {name && <span className="text-body-02 text-grey-02 truncate">{name}</span>}
          </div>
          <div className="flex items-center gap-sm">
            {readOnly ? (
              <Button size="md" onClick={clone} loading={busy} disabled={busy}>
                Clone to edit
              </Button>
            ) : (
              <>
                {id && (
                  <Button
                    variant="outlined"
                    size="md"
                    onClick={publish}
                    loading={busy}
                    disabled={busy || lines.length === 0 || !totalOk}
                    title={totalOk ? undefined : 'Allocations must sum to 100% to publish'}
                  >
                    Publish
                  </Button>
                )}
                <Button
                  size="md"
                  onClick={() => {
                    setSaveError(null);
                    setSaveOpen(true);
                  }}
                  disabled={busy || lines.length === 0}
                  data-testid="save-portfolio-button"
                >
                  Save This Portfolio
                </Button>
              </>
            )}
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {notice && (
          <Alert variant="info" dismissible onDismiss={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {!readOnly && (
          <div className="max-w-form">
            <FundSearchSelect
              onSelect={addFund}
              placeholder="Add a fund to the portfolio…"
              testId="portfolio-fund-search"
            />
          </div>
        )}

        <DataTable
          columns={columns}
          data={lines}
          rowKey={(l) => l.fund.fundKey}
          emptyState={
            <span>
              No funds yet — search above, or select funds on the Fund Center and use
              “Portfolio Builder”.
            </span>
          }
        />

        <div className="flex items-center gap-sm" data-testid="portfolio-total">
          <span className="text-body-03 text-grey-02">Total allocation</span>
          <Badge color={totalOk ? 'green' : 'yellow'} size="md">
            {totalWeight.toFixed(2)}%
          </Badge>
          {!totalOk && lines.length > 0 && (
            <span className="text-body-04 text-grey-02">must reach 100% to publish</span>
          )}
          {lines.length > 0 && (
            <span className="flex items-center gap-xs ml-md" data-testid="portfolio-builder-risk">
              <span className="text-body-03 text-grey-02">Indicative risk</span>
              <PortfolioRiskBadge result={riskResult} />
            </span>
          )}
        </div>
      </div>

      {chartData.data.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md">
          <h2 className="m-0 text-body-02 font-medium">Fund Returns</h2>
          <p className="m-0 text-body-04 text-grey-02">
            Calendar-year performance (%) per fund — daily series arrives with the fund
            snapshots follow-up.
          </p>
          <div data-testid="portfolio-returns-chart">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={(v: number) => `${v}%`} />
                <Tooltip formatter={(v) => `${String(v)}%`} />
                <Legend />
                {chartData.series.map((s, i) => (
                  <Line
                    key={s}
                    type="monotone"
                    dataKey={s}
                    stroke={chartPalette[i % chartPalette.length]}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {saveOpen && (
        <Modal open onClose={() => (busy ? undefined : setSaveOpen(false))} size="small">
          <Modal.Header title="Save This Portfolio" onClose={() => setSaveOpen(false)} />
          <Modal.Content>
            <div className="flex flex-col gap-md">
              <TextInput
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Balanced Growth 2026"
                required
              />
              <TextArea
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
              <Dropdown
                label="Risk profile"
                value={riskProfile}
                options={RISK_OPTIONS}
                onChange={setRiskProfile}
              />
              {saveError && <Alert variant="error">{saveError}</Alert>}
              <div className="flex justify-end gap-sm">
                <Button variant="outlined" size="md" onClick={() => setSaveOpen(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  size="md"
                  onClick={save}
                  loading={busy}
                  disabled={busy || name.trim() === ''}
                  data-testid="confirm-save-portfolio"
                >
                  Save draft
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}

function buildChartData(lines: BuilderLine[]): {
  data: Array<Record<string, number | string>>;
  series: string[];
} {
  const withYears = lines.filter(
    (l) => l.fund.calendarYears && Object.keys(l.fund.calendarYears).length > 0
  );
  if (withYears.length === 0) return { data: [], series: [] };

  const years = new Set<string>();
  withYears.forEach((l) => Object.keys(l.fund.calendarYears!).forEach((y) => years.add(y)));

  const data = Array.from(years)
    .sort()
    .map((year) => {
      const row: Record<string, number | string> = { year };
      withYears.forEach((l) => {
        const raw = l.fund.calendarYears![year];
        const parsed = Number(raw);
        if (raw != null && raw !== '' && Number.isFinite(parsed)) {
          row[l.fund.fundName] = parsed;
        }
      });
      return row;
    });

  return { data, series: withYears.map((l) => l.fund.fundName) };
}

function extractApiMessage(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
  if (data?.message) return data.message;
  return e instanceof Error ? e.message : fallback;
}
